import React, { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * W7 L3 — Atelier de curation V1 : picker pour choisir UNE sous-catégorie alternative
 * dans une liste cherchable (résout les ~138 entrées sans pills surchargés). Réutilise
 * `subcategoriesList` déjà chargé par useCurationWorkbench (pas de re-fetch).
 *
 * Inspiré du pattern checkbox dans boucle de FactorFormModal:156-163, mais extrait en
 * composant réutilisable avec sélection unitaire (1 code -> onSelect(subcat)).
 *
 * Props :
 * - subcategoriesList : [{code, name_fr}]
 * - action : "add" | "remove" (cosmétique : titre + couleur du bouton).
 * - onSelect(subcat)  : appelé quand l'utilisateur choisit une subcat.
 * - onCancel()        : ferme le picker sans choix.
 * - isDark
 */
const AltSubcatPicker = ({ subcategoriesList, action, onSelect, onCancel, isDark }) => {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subcategoriesList;
    return subcategoriesList.filter(sc =>
      (sc.name_fr || '').toLowerCase().includes(q) ||
      (sc.code || '').toLowerCase().includes(q)
    );
  }, [search, subcategoriesList]);

  const isAdd = action === 'add';
  const title = t(isAdd ? 'curation.altSubcat.addTitle' : 'curation.altSubcat.removeTitle');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onCancel}
      data-testid="alt-subcat-picker-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-md mx-4 rounded-2xl shadow-2xl p-6 ${isDark ? 'bg-slate-800' : 'bg-white'}`}
        data-testid="alt-subcat-picker"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
          <button onClick={onCancel} data-testid="alt-subcat-cancel-btn"
            className={`p-1 rounded ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative mb-3">
          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
          <input
            type="text"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('curation.altSubcat.searchPlaceholder')}
            data-testid="alt-subcat-search-input"
            className={`w-full pl-8 pr-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500' : 'bg-white border-gray-300 placeholder:text-gray-400'}`}
          />
        </div>

        <div
          className={`max-h-72 overflow-y-auto rounded-lg border ${isDark ? 'border-slate-700' : 'border-gray-200'}`}
          data-testid="alt-subcat-list"
        >
          {filtered.length === 0 ? (
            <p className={`text-xs p-3 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} data-testid="alt-subcat-empty">
              {t('curation.altSubcat.noMatch')}
            </p>
          ) : (
            filtered.map((sc) => (
              <button
                key={sc.code}
                onClick={() => onSelect(sc)}
                data-testid={`alt-subcat-option-${sc.code}`}
                className={`w-full text-left px-3 py-2 text-xs border-b last:border-b-0 transition-colors ${
                  isDark
                    ? 'border-slate-700 hover:bg-slate-700 text-slate-200'
                    : 'border-gray-100 hover:bg-gray-50 text-gray-700'
                }`}
              >
                <span className="font-medium">{sc.name_fr}</span>
                <span className={`ml-2 text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {sc.code}
                </span>
              </button>
            ))
          )}
        </div>

        <p className={`text-[10px] mt-3 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
          {filtered.length} {filtered.length > 1 ? 'résultats' : 'résultat'} — clique pour confirmer.
        </p>
      </div>
    </div>
  );
};

export default AltSubcatPicker;
